import { inject } from '@angular/core';
import { CanActivateFn, Router, type UrlTree } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { AuthStateService } from '../../auth-core/services/auth-state.service';
import { AUTH_LOGGER } from '../../auth-core/tokens/auth-logger.token';
import type { IPermissionGuardData } from '../interfaces/authorization.interfaces';
import type { DenyBehavior } from '../models/authorization-context.model';
import { AuthorizationService } from '../services/authorization.service';
import { OfflineAuthService } from '../../../offline-auth/offline-auth.service';
import { checkNetworkOnline } from '../../../offline-auth/network-connectivity.util';
import { LOOKUP_API_BASE_URL } from '../../../http/api-client.tokens';
import { StatusIndicatorService } from '@complexcare/harmony';

function resolveDeniedNavigation(router: Router, onDenied: DenyBehavior, deniedRedirectTo?: string): UrlTree | false {
  if (onDenied === 'redirect-login') {
    return router.parseUrl('/login');
  }

  if (onDenied === 'redirect-unauthorized') {
    return router.parseUrl(deniedRedirectTo ?? '/unauthorized');
  }

  return false;
}

/**
 * Claims-first permission guard. Route data should conform to IPermissionGuardData. Returns true when all provided
 * role/permission checks pass, otherwise false.
 *
 * Expected route data:
 *
 * - `requiredPermissions?: string[]` - ALL must be present (AND logic)
 * - `requiredAnyPermissions?: string[]` - ANY must be present (OR logic)
 * - `requiredRoles?: string[]` - ALL must be present (AND logic)
 * - `requiredAnyRoles?: string[]` - ANY must be present (OR logic)
 *
 * All provided checks must pass (combined with AND logic). If no checks provided, access is granted. Permission denials
 * are logged for audit purposes.
 */
export const permissionGuard: CanActivateFn = async (route) => {
  const authState = inject(AuthStateService);
  const authorizationService = inject(AuthorizationService);
  const router = inject(Router);
  const logger = inject(AUTH_LOGGER, { optional: true });
  const lookupApiUrl = inject(LOOKUP_API_BASE_URL, { optional: true });
  const offlineAuth = inject(OfflineAuthService, { optional: true });
  const statusIndicator = inject(StatusIndicatorService, { optional: true });

  const guardData = route.data as IPermissionGuardData;
  const requiredPermissions = guardData?.requiredPermissions ?? [];
  const requiredAnyPermissions = guardData?.requiredAnyPermissions ?? [];
  const requiredRoles = guardData?.requiredRoles ?? [];
  const requiredAnyRoles = guardData?.requiredAnyRoles ?? [];
  const onDenied: DenyBehavior = guardData?.onDenied ?? 'redirect-unauthorized';
  const deniedRedirectTo = guardData?.deniedRedirectTo;

  const targetRoute = route.url.map((s) => s.path).join('/');

  // When offline (verified via health probe) or authenticated offline via PIN, access is governed by offlineGuard
  const isOnline = await checkNetworkOnline(lookupApiUrl);
  // Only confirmed-online results are synced here; offline transitions belong to the monitoring probe.
  if (isOnline) statusIndicator?.reportProbeResult(true);
  const isOfflineAuth = offlineAuth?.offlineAuthenticated();

  if (!isOnline || isOfflineAuth) {
    return true;
  }

  // Check if any checks are provided; if none, grant access
  const hasAnyGuardData =
    requiredPermissions.length > 0 ||
    requiredAnyPermissions.length > 0 ||
    requiredRoles.length > 0 ||
    requiredAnyRoles.length > 0;

  if (!hasAnyGuardData) {
    return true;
  }

  return await firstValueFrom(
    authState.session$.pipe(
      filter((session) => session.status !== 'signing-in'),
      take(1),
      timeout(20_000),
      map((session) => {
        if (session.status !== 'signed-in') {
          logger?.warn('[conductor/auth] Permission denied: user not signed in', {
            route: targetRoute,
            timestamp: new Date().toISOString(),
          });
          return resolveDeniedNavigation(router, onDenied, deniedRedirectTo);
        }

        const context = authorizationService.context();

        const denialReasons: string[] = [];

        // Check requiredPermissions (ALL must be present)
        if (requiredPermissions.length > 0) {
          if (!authorizationService.hasAllPermissions(requiredPermissions)) {
            denialReasons.push(`missing-permissions: [${requiredPermissions.join(', ')}]`);
          }
        }

        // Check requiredAnyPermissions (ANY must be present or match prefix)
        if (requiredAnyPermissions.length > 0) {
          const hasWildcardPrefix = requiredAnyPermissions.some((perm) => perm.endsWith('.*'));

          if (hasWildcardPrefix) {
            // Use prefix matching for wildcards like 'Admin.*'
            if (!authorizationService.hasAnyPermissionPrefix(requiredAnyPermissions)) {
              denialReasons.push(`insufficient-any-permissions-prefix: [${requiredAnyPermissions.join(', ')}]`);
            }
          } else {
            // Use exact matching for specific permissions
            if (!authorizationService.hasAnyPermission(requiredAnyPermissions)) {
              denialReasons.push(`insufficient-any-permissions: [${requiredAnyPermissions.join(', ')}]`);
            }
          }
        }

        // Check requiredRoles (ALL must be present)
        if (requiredRoles.length > 0) {
          if (!authorizationService.hasAllRoles(requiredRoles)) {
            denialReasons.push(`missing-roles: [${requiredRoles.join(', ')}]`);
          }
        }

        // Check requiredAnyRoles (ANY must be present)
        if (requiredAnyRoles.length > 0) {
          if (!authorizationService.hasAnyRole(requiredAnyRoles)) {
            denialReasons.push(`insufficient-any-roles: [${requiredAnyRoles.join(', ')}]`);
          }
        }

        // All checks passed
        if (denialReasons.length === 0) {
          return true;
        }

        // Log permission denial for audit trail
        logger?.warn('[conductor/auth] Permission denied', {
          userId: session.subjectId,
          route: targetRoute,
          denialReasons,
          userRoles: context.roles,
          userPermissions: context.permissions,
          requiredPermissions,
          requiredAnyPermissions,
          requiredRoles,
          requiredAnyRoles,
          timestamp: new Date().toISOString(),
        });

        return resolveDeniedNavigation(router, onDenied, deniedRedirectTo);
      }),
      catchError((err) => {
        logger?.warn('[conductor/auth] Permission guard session wait timed out or failed:', err);
        return of(resolveDeniedNavigation(router, onDenied, deniedRedirectTo));
      })
    )
  );
};
